import { Link } from 'react-router-dom';
import { ArrowRight, Code, Cpu, Cloud, BookOpen, CheckCircle, TrendingUp, Users, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Aurora from '@/components/Aurora';
import AnimatedHeroText from '@/components/AnimatedHeroText';
import TechStack from '@/components/TechStack';
import ScrollReveal, { StaggerGroup } from '@/components/ScrollReveal';
import AnimatedCounter from '@/components/AnimatedCounter';
import TestimonialCarousel from '@/components/TestimonialCarousel';
import StarBorderButton from '@/components/StarBorderButton';
import SpotlightCard from '@/components/SpotlightCard';

export default function Home() {
  const services = [
    {
      icon: <Code size={32} />,
      title: 'Web Development',
      description: 'Jasa Full-stack development dari frontend hingga backend dengan teknologi modern.'
    },
    {
      icon: <Cpu size={32} />,
      title: 'IoT Development',
      description: 'Jasa pengerjaan proyek IoT dengan ESP32/ESP8266, sensor telemetry, dan integrasi dengan website.'
    },
    {
      icon: <Cloud size={32} />,
      title: 'DevOps & Cloud',
      description: 'Deploy, monitoring, dan maintenance aplikasi di AWS, DigitalOcean, atau Vercel.'
    },
    {
      icon: <BookOpen size={32} />,
      title: 'Engineering Support',
      description: 'Pengerjaan Tugas MATLAB, AutoCAD, Fusion 360, desain PCB, dan project teknik lainnya.'
    }
  ];

  const advantages = [
    'Mahasiswa Teknik Elektro dengan hands-on experience',
    'Menjembatani teori kampus dengan praktik industri',
    'Pendampingan personal yang fleksibel',
    'Berfokus pada hasil dan pembelajaran'
  ];

  const workflow = [
    { step: '01', title: 'Konsultasi', desc: 'Diskusi kebutuhan & tujuan project' },
    { step: '02', title: 'Sketsa', desc: 'Estimasi waktu, cost & teknologi' },
    { step: '03', title: 'Eksekusi', desc: 'Development & regular updates' },
    { step: '04', title: 'Handover', desc: 'Dokumentasi & support lanjutan' }
  ];

  const stats = [
    { icon: <Users size={24} />, value: '30+', label: 'Klien Puas' },
    { icon: <Award size={24} />, value: '40+', label: 'Project Selesai' },
    { icon: <TrendingUp size={24} />, value: '100%', label: 'Satisfaction Rate' }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-slate-950" data-testid="hero-section">
        {/* Aurora Background */}
        <Aurora />
        
        {/* Gradient Overlay untuk depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/40 via-transparent to-slate-950/60 pointer-events-none"></div>
        
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium mb-4">
              Web • IoT • DevOps • Engineering
            </div>
            
            <div className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="hero-title">
              <AnimatedHeroText 
                line1="Jasa Pengerjaan Tugas Teknik, Web & IoT Project"
                line2="untuk Mahasiswa & UMKM"
              />
            </div>
            
            <p className="text-lg text-slate-300 max-w-2xl mx-auto" data-testid="hero-subtitle">
              Solusi tugas teknik end-to-end dari web development, IoT, DevOps, hingga mentoring teknis. 
              Bangun project teknik dengan pendampingan praktis dan hasil nyata.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <a href="https://wa.me/6285183347683" target="_blank" rel="noopener noreferrer">
                <StarBorderButton className="btn-primary text-base px-8 py-6" data-testid="hero-cta-whatsapp">
                  Konsultasi Gratis via WhatsApp
                  <ArrowRight size={20} />
                </StarBorderButton>
              </a>
              <Link to="/services">
                <Button variant="outline" className="btn-secondary text-base px-8 py-6" data-testid="hero-cta-services">
                  Lihat Layanan
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 px-6 bg-slate-900/50" data-testid="services-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Apa yang Saya Kerjakan?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Layanan jasa pengerjaan tugas teknik komprehensif yang dirancang untuk membantu project teknik Anda
            </p>
          </div>

          <StaggerGroup 
            staggerDelay={0.15} 
            animation="fade-up"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {services.map((service, index) => (
              <SpotlightCard 
                key={index} 
                className="glass-effect p-6 card-hover border-slate-700 group transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/20" 
                data-testid={`service-card-${index}`}
              >
                <div className="text-cyan-400 mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2 transition-colors duration-300 group-hover:text-cyan-400" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {service.title}
                </h3>
                <p className="text-slate-400 text-sm">{service.description}</p>
              </SpotlightCard>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Tech Stack Section */}
      <TechStack />

      {/* Advantages */}
      <section className="py-20 px-6" data-testid="advantages-section">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <ScrollReveal animation="fade-right">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Kenapa Pilih Arnata?
              </h2>
              <StaggerGroup staggerDelay={0.1} animation="fade-right" className="space-y-4">
                {advantages.map((advantage, index) => (
                  <div key={index} className="flex items-start space-x-3 group" data-testid={`advantage-${index}`}>
                    <CheckCircle size={24} className="text-cyan-400 flex-shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-125 group-hover:rotate-12" />
                    <p className="text-slate-300 transition-colors duration-300 group-hover:text-white">{advantage}</p>
                  </div>
                ))}
              </StaggerGroup>
              <div className="mt-8">
                <Link to="/about">
                  <Button className="btn-primary group" data-testid="about-cta">
                    Kenali Lebih Lanjut
                    <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </ScrollReveal>

            <StaggerGroup staggerDelay={0.15} animation="fade-left" className="grid grid-cols-1 gap-4">
              {stats.map((stat, index) => (
                <SpotlightCard key={index} className="glass-effect p-6 border-slate-700 group hover:border-cyan-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/20" data-testid={`stat-card-${index}`}>
                  <div className="flex items-center space-x-4">
                    <div className="text-cyan-400 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                      {stat.icon}
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        <AnimatedCounter end={stat.value} duration={2500} />
                      </div>
                      <div className="text-slate-400 text-sm transition-colors duration-300 group-hover:text-slate-300">
                        {stat.label}
                      </div>
                    </div>
                  </div>
                </SpotlightCard>
              ))}
            </StaggerGroup>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-900/50 overflow-hidden" data-testid="testimonials-section">
        <div className="container mx-auto px-6">
          <ScrollReveal animation="fade-up" className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Apa Kata Mereka?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Testimoni dari klien yang puas dengan hasil kolaborasi kami
            </p>
          </ScrollReveal>
        </div>

        <ScrollReveal animation="fade-up" delay={0.2}>
          <TestimonialCarousel />
        </ScrollReveal>

        <div className="container mx-auto px-6">
          <ScrollReveal animation="fade-up" delay={0.4} className="text-center mt-12">
            <p className="text-slate-400 text-sm mb-6">
              Ingin menjadi bagian dari klien yang puas? Mari diskusikan project Anda!
            </p>
            <Link to="/contact">
              <StarBorderButton className="btn-primary group">
                Mulai Konsultasi Gratis
                <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
              </StarBorderButton>
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-20 px-6" data-testid="workflow-section">
        <div className="container mx-auto">
          <ScrollReveal animation="fade-up" className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Cara Kerja Kami
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Proses yang terstruktur dan transparan dari konsultasi hingga handover
            </p>
          </ScrollReveal>

          <StaggerGroup 
            staggerDelay={0.2} 
            animation="scale-up"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {workflow.map((step, index) => (
              <SpotlightCard key={index} className="glass-effect p-6 border-slate-700 relative group hover:border-cyan-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/20" data-testid={`workflow-step-${index}`}>
                <div className="text-5xl font-bold text-cyan-400/20 absolute top-4 right-4 transition-all duration-300 group-hover:text-cyan-400/40 group-hover:scale-110" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {step.step}
                </div>
                <div className="relative z-10">
                  <h3 className="text-xl font-semibold text-white mb-2 transition-colors duration-300 group-hover:text-cyan-400" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {step.title}
                  </h3>
                  <p className="text-slate-400 text-sm transition-colors duration-300 group-hover:text-slate-300">{step.desc}</p>
                </div>
              </SpotlightCard>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 px-6 overflow-hidden bg-slate-950" data-testid="cta-section">
        {/* Aurora Background - More Subtle */}
        <div className="absolute inset-0" style={{ opacity: 0.6 }}>
          <Aurora />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/60 via-slate-950/20 to-slate-950/60 pointer-events-none"></div>
        
        <div className="container mx-auto relative z-10">
          <ScrollReveal animation="scale" threshold={0.2}>
            <Card className="glass-effect border-slate-700 p-6 sm:p-12 text-center hover:border-cyan-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-cyan-500/30">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Siap Memulai Project Anda?
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto mb-8">
                Ceritakan kebutuhan Anda, kami bantu breakdown teknis dan solusinya. 
                Gratis konsultasi awal!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch">
                <Link to="/contact" className="w-full sm:w-auto">
                  <StarBorderButton className="btn-primary text-base px-8 py-6 group w-full" data-testid="cta-contact">
                    Mulai Konsultasi
                    <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </StarBorderButton>
                </Link>
                <Link to="/portfolio" className="w-full sm:w-auto">
                  <Button variant="outline" className="btn-secondary text-base px-8 py-6 group w-full" data-testid="cta-portfolio">
                    Lihat Portfolio
                  </Button>
                </Link>
              </div>
            </Card>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}